import type { WorkItemPhase, WorkItemPriority } from "@yagni/shared";
import {
  Circle,
  Loader,
  CheckCircle2,
  Ban,
  Archive,
  GitBranch,
  ArrowUp,
  AlertTriangle,
  Minus,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   Work Item Constants — single source of truth
   ═══════════════════════════════════════════════════════════ */

// Display lexicon (2026-07-02): the nine internal phases render as four
// visible words — Proposed / Needs you / In progress / Done (+ Closed).
// The full phase machine stays internal; users read status, not taxonomy.
export const PHASE_LABELS: Record<WorkItemPhase, string> = {
  proposed: "Proposed",
  accepted: "In progress",
  planning: "In progress",
  awaiting_execution_approval: "Needs you",
  executing: "In progress",
  needs_input: "Needs you",
  final_review: "In progress",
  done: "Done",
  dismissed: "Closed",
  split: "Split",
};

export const PHASE_ICONS: Record<WorkItemPhase, React.ReactNode> = {
  proposed: <Circle size={14} className="text-[var(--accent)]" />,
  accepted: <Circle size={14} className="text-[var(--text-secondary)]" />,
  planning: <Loader size={14} className="text-[var(--accent-active)]" />,
  awaiting_execution_approval: <Ban size={14} className="text-[var(--warning)]" />,
  executing: <Loader size={14} className="text-[var(--accent-active)]" />,
  needs_input: <Ban size={14} className="text-[var(--warning)]" />,
  final_review: <Circle size={14} className="text-[var(--accent)]" />,
  done: <CheckCircle2 size={14} className="text-[var(--success)]" />,
  dismissed: <Archive size={14} className="text-[var(--text-tertiary)]" />,
  split: <GitBranch size={14} className="text-[var(--text-secondary)]" />,
};

export const PHASE_COLORS: Record<WorkItemPhase, { bg: string; text: string }> = {
  proposed: { bg: "var(--accent-muted)", text: "var(--accent)" },
  accepted: { bg: "var(--border-subtle)", text: "var(--text-secondary)" },
  planning: { bg: "var(--accent-active-muted)", text: "var(--accent-active)" },
  awaiting_execution_approval: { bg: "var(--warning-muted)", text: "var(--warning)" },
  executing: { bg: "var(--accent-active-muted)", text: "var(--accent-active)" },
  needs_input: { bg: "var(--warning-muted)", text: "var(--warning)" },
  final_review: { bg: "var(--accent-muted)", text: "var(--accent)" },
  done: { bg: "var(--success-muted)", text: "var(--success)" },
  dismissed: { bg: "var(--border-subtle)", text: "var(--text-tertiary)" },
  split: { bg: "var(--border-subtle)", text: "var(--text-secondary)" },
};

export const PRIORITY_LABELS: Record<WorkItemPriority, string> = {
  normal: "Normal",
  high: "High",
  urgent: "Urgent",
};

export const PRIORITY_ORDER: Record<WorkItemPriority, number> = {
  urgent: 0,
  high: 1,
  normal: 2,
};

export const PRIORITY_ICONS: Record<WorkItemPriority, React.ReactNode> = {
  normal: <Minus size={14} className="text-[var(--text-tertiary)]" />,
  high: <ArrowUp size={14} className="text-[var(--accent)]" />,
  urgent: <AlertTriangle size={14} className="text-[var(--error)]" />,
};

export const AGENT_COLORS: Record<string, string> = {
  yagni: "var(--agent-dev)",
  ops: "var(--agent-ops)",
  pm: "var(--agent-pm)",
  dev: "var(--agent-dev)",
  analytics: "var(--agent-analytics)",
  comms: "var(--agent-comms)",
  gtm: "var(--agent-gtm)",
  qa: "var(--agent-qa)",
  support: "var(--agent-support)",
  finance: "var(--agent-finance)",
  design: "var(--agent-design)",
};

export function getAgentColor(name: string | null): string {
  if (!name) return "var(--text-tertiary)";
  const key = name.toLowerCase().replace(/^@/, "").replace(/^agent-/, "");
  return AGENT_COLORS[key] ?? "var(--text-tertiary)";
}
